import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Clock, User, MapPin, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeeClock = () => {
  const [phoneLast4, setPhoneLast4] = useState('');
  const [configuredLocation, setConfiguredLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [setupEmail, setSetupEmail] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [selectedLocationForSetup, setSelectedLocationForSetup] = useState('');
  const [employeeStatus, setEmployeeStatus] = useState(null); // null, 'clocked_in', 'clocked_out'
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionComments, setExceptionComments] = useState('');
  const [exceptionClockIn, setExceptionClockIn] = useState('');
  const [exceptionClockOut, setExceptionClockOut] = useState('');

  useEffect(() => {
    fetchLocations();
    loadConfiguredLocation();
    
    // Update clock every second
    const clockTimer = setInterval(() => setCurrentTime(new Date()), 1000);
    
    // Auto-refresh locations and employee data every 30 seconds
    const refreshTimer = setInterval(() => {
      fetchLocations();
    }, 30000); // 30 seconds
    
    return () => {
      clearInterval(clockTimer);
      clearInterval(refreshTimer);
    };
  }, []);

  const loadConfiguredLocation = () => {
    const savedLocation = localStorage.getItem('terminalLocation');
    if (savedLocation) {
      setConfiguredLocation(JSON.parse(savedLocation));
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const checkEmployeeStatus = async (phone) => {
    if (!phone || phone.length !== 4) {
      setEmployeeStatus(null);
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API}/clock/status/${phone}`);
      setEmployeeStatus(response.data.status);
    } catch (error) {
      // Employee not found or error - reset status
      setEmployeeStatus(null);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handlePhoneChange = (value) => {
    const cleaned = value.replace(/\D/g, '');
    setPhoneLast4(cleaned);
    
    // Check status when 4 digits are entered
    if (cleaned.length === 4) {
      checkEmployeeStatus(cleaned);
    } else {
      setEmployeeStatus(null);
    }
  };

  const handleSetupTerminal = async () => {
    if (!setupEmail || !setupPassword || !selectedLocationForSetup) {
      toast.error('Please enter email, password and select a location');
      return;
    }

    // Verify manager credentials
    try {
      const response = await axios.post(`${API}/auth/manager/login`, {
        email: setupEmail,
        pin: setupPassword,
      });

      // Check if user is Manager or Administrator
      if (response.data.manager.role !== 'Manager' && response.data.manager.role !== 'Administrator') {
        toast.error('Only Managers and Administrators can configure terminals');
        return;
      }

      const location = locations.find((loc) => loc.id === selectedLocationForSetup);
      localStorage.setItem('terminalLocation', JSON.stringify(location));
      setConfiguredLocation(location);
      setShowSetupDialog(false);
      setSetupEmail('');
      setSetupPassword('');
      setSelectedLocationForSetup('');
      toast.success(`Terminal configured for ${location.name}`);
    } catch (error) {
      toast.error('Invalid credentials. Only managers can configure terminals.');
    }
  };

  const handlePunch = async () => {
    if (!phoneLast4 || phoneLast4.length !== 4) {
      toast.error('Please enter the last 4 digits of your phone number');
      return;
    }

    if (!configuredLocation) {
      toast.error('Terminal not configured. Please contact your manager.');
      setShowSetupDialog(true);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/clock/punch`, {
        phone_last4: phoneLast4,
        location_id: configuredLocation.id,
      });

      const data = response.data;

      if (data.action === 'clock_in') {
        toast.success(`Welcome ${data.employee_name}! You are clocked in.`);
      } else {
        toast.success(
          `Goodbye ${data.employee_name}! You worked ${data.total_hours} hours today.`
        );
      }

      setPhoneLast4('');
      setEmployeeStatus(null); // Reset status after punch
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Error processing punch. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExceptionRequest = () => {
    if (!phoneLast4 || phoneLast4.length !== 4) {
      toast.error('Please enter your phone number (last 4 digits)');
      return;
    }
    
    setShowExceptionDialog(true);
  };

  const submitExceptionRequest = async () => {
    if (!exceptionReason) {
      toast.error('Please select a reason for the exception');
      return;
    }

    if (!exceptionComments.trim()) {
      toast.error('Please provide additional details in the comments');
      return;
    }

    if (!configuredLocation) {
      toast.error('Terminal location not configured. Please contact your manager.');
      return;
    }

    try {
      await axios.post(`${API}/clock/exception`, {
        phone_last4: phoneLast4,
        reason: `${exceptionReason} - ${exceptionComments}`,
        location_id: configuredLocation.id,  // Send current terminal location
      });
      
      toast.success('Exception submitted! A manager will review your explanation. Remember: This does not punch you in or out.');
      setShowExceptionDialog(false);
      setExceptionReason('');
      setExceptionComments('');
      setPhoneLast4('');
      setEmployeeStatus(null);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          'Error submitting exception request. Please try again.'
      );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card
        className="w-full max-w-md p-8 bg-white/95 backdrop-blur-sm shadow-2xl"
        data-testid="employee-clock-card"
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-900 to-black rounded-full flex items-center justify-center shadow-lg">
              <Clock className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Punch Clock</h1>
          <p className="text-5xl font-bold text-gray-900 mb-1">
            {currentTime.toLocaleTimeString()}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {currentTime.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
          
          {configuredLocation && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {configuredLocation.name}
              </h2>
              <button
                onClick={() => setShowSetupDialog(true)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
                data-testid="reconfigure-link"
              >
                Change Location
              </button>
            </div>
          )}
        </div>

        {!configuredLocation && (
          <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
            <p className="text-sm text-orange-800 text-center">
              Terminal not configured. Please contact your manager.
            </p>
            <Button
              onClick={() => setShowSetupDialog(true)}
              variant="outline"
              className="w-full mt-3"
              data-testid="configure-terminal-button"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configure Terminal
            </Button>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Phone Number (Last 4 Digits)
            </label>
            <Input
              type="text"
              maxLength="4"
              placeholder="1234"
              value={phoneLast4}
              onChange={(e) => handlePhoneChange(e.target.value)}
              className="text-2xl text-center tracking-widest h-14"
              data-testid="phone-input"
            />
          </div>

          <Button
            onClick={handlePunch}
            disabled={loading || !configuredLocation || checkingStatus}
            className={`w-full h-16 text-xl font-semibold text-white shadow-lg transition-all ${
              employeeStatus === 'clocked_in'
                ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                : employeeStatus === 'clocked_out'
                ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                : 'bg-gradient-to-r from-gray-900 to-black hover:from-gray-800 hover:to-gray-900'
            }`}
            data-testid="punch-button"
          >
            {loading
              ? 'Processing...'
              : checkingStatus
              ? 'Checking...'
              : employeeStatus === 'clocked_in'
              ? 'Punch OUT'
              : employeeStatus === 'clocked_out'
              ? 'Punch IN'
              : 'Punch Clock'}
          </Button>

          <Button
            onClick={handleExceptionRequest}
            variant="outline"
            className="w-full h-12 border-2 border-gray-800 hover:bg-gray-100"
            data-testid="exception-request-button"
          >
            Request Exception
          </Button>

          <div className="text-center pt-4">
            <a
              href="/manager/login"
              className="text-sm text-gray-800 hover:text-black underline font-medium"
              data-testid="manager-login-link"
            >
              Manager Login
            </a>
          </div>
        </div>
      </Card>

      {/* Terminal Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent data-testid="terminal-setup-dialog">
          <DialogHeader>
            <DialogTitle>Configure Terminal Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              Manager or Administrator authorization required to configure this terminal.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Manager Email
              </label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={setupEmail}
                onChange={(e) => setSetupEmail(e.target.value)}
                data-testid="setup-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Manager Password
              </label>
              <Input
                type="password"
                placeholder="Enter your password"
                value={setupPassword}
                onChange={(e) => setSetupPassword(e.target.value)}
                data-testid="setup-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Location for This Terminal
              </label>
              <Select
                value={selectedLocationForSetup}
                onValueChange={setSelectedLocationForSetup}
              >
                <SelectTrigger data-testid="setup-location-select">
                  <SelectValue placeholder="Choose store location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleSetupTerminal}
              className="w-full"
              data-testid="submit-terminal-setup"
            >
              Configure Terminal
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exception Request Dialog */}
      <Dialog open={showExceptionDialog} onOpenChange={setShowExceptionDialog}>
        <DialogContent data-testid="exception-request-dialog">
          <DialogHeader>
            <DialogTitle>Request Exception</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
              <p className="text-sm text-blue-900 font-medium mb-2">
                ⓘ Important: Exceptions are explanations only
              </p>
              <p className="text-sm text-blue-800">
                This does NOT punch you IN or OUT from Punchclock. A manager will review your explanation and make time adjustments if needed.
              </p>
            </div>
            <p className="text-sm text-gray-600">
              Submit an explanation if you forgot to clock in or out. Include details about your actual work hours.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason
              </label>
              <Select
                value={exceptionReason}
                onValueChange={setExceptionReason}
              >
                <SelectTrigger data-testid="exception-reason-select">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Forgot to punch in">Forgot to punch in</SelectItem>
                  <SelectItem value="Forgot to punch out">Forgot to punch out</SelectItem>
                  <SelectItem value="Forgot to punch in and out">Forgot to punch in and out</SelectItem>
                  <SelectItem value="System error">System error</SelectItem>
                  <SelectItem value="Technical issue">Technical issue</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Details
              </label>
              <textarea
                placeholder="E.g., I started at 9am and finished at 5pm but forgot to punch in"
                value={exceptionComments}
                onChange={(e) => setExceptionComments(e.target.value)}
                className="w-full min-h-[120px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                data-testid="exception-comments-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include your actual work hours (e.g., &quot;Started at 9:00am, finished at 5:00pm&quot;)
              </p>
            </div>
            <Button
              onClick={submitExceptionRequest}
              className="w-full bg-gradient-to-r from-gray-900 to-black hover:from-gray-800 hover:to-gray-900"
              data-testid="submit-exception-request"
            >
              Submit Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeClock;